import { useAuth } from "app/Auth/AuthBoundary";
import { LoginContainer } from "app/styles";
import { useInterval } from "features/CenterContent/shared/NetworkToastProvider";
import { mailLinkWithError } from "features/Login/Login";
import { TFunction } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Heading,
  LightSpinner,
  TextLink,
} from "shared/components/Spor";
import styled from "styled-components";

enum RetryStatus {
  IDLE,
  COUNTDOWN,
  ACTIVE,
  FAILED,
}

type RetryState = {
  status: RetryStatus;
  attemptNumber: number;
  cooldownRemaining: number;
};

const LightSpinnerStyled = styled(LightSpinner)`
  path {
    fill: ${({ theme }) => theme.colorTextMain};
    stroke: ${({ theme }) => theme.colorTextMain};
  }
`;

const INITIAL_COUNTDOWN = 5;

function retryText(
  state: RetryState,
  translate: TFunction<"translation", undefined>,
): string {
  switch (state.status) {
    case RetryStatus.IDLE:
      return "...";
    case RetryStatus.COUNTDOWN:
      return translate(
        "Prøver igjen om {{cooldownRemaining}} sekunder... Forsøk {{attemptNumber}}/5",
        {
          cooldownRemaining: state.cooldownRemaining,
          attemptNumber: state.attemptNumber,
        },
      );
    case RetryStatus.ACTIVE:
      return translate("Prøver igjen... Forsøk {{attemptNumber}}/5", {
        attemptNumber: state.attemptNumber,
      });
    case RetryStatus.FAILED:
      return translate("Beklager");
  }
}

/**
 * For Network Error we attempt a retry 5 times before giving up
 */
const NetworkError = ({ error }: { error: Error }) => {
  const { t } = useTranslation();
  const { subscribe } = useAuth();

  const [retryState, setRetryState] = useState<RetryState>({
    status: RetryStatus.COUNTDOWN,
    attemptNumber: 1,
    cooldownRemaining: INITIAL_COUNTDOWN,
  });

  const callCheckUserPermissions = useCallback(() => {
    try {
      subscribe();
    } finally {
      setRetryState({
        status: RetryStatus.IDLE,
        attemptNumber: retryState.attemptNumber + 1,
        cooldownRemaining: INITIAL_COUNTDOWN,
      });
    }
  }, [retryState]);

  useInterval(
    () => {
      if (retryState.cooldownRemaining > 0) {
        setRetryState({
          ...retryState,
          cooldownRemaining: retryState.cooldownRemaining - 1,
        });
      } else {
        setRetryState({ ...retryState, status: RetryStatus.ACTIVE });
      }
    },
    retryState.status === RetryStatus.COUNTDOWN ? 1000 : null,
  );

  useEffect(() => {
    switch (retryState.status) {
      case RetryStatus.IDLE:
        if (retryState.attemptNumber < 6) {
          setRetryState({ ...retryState, status: RetryStatus.COUNTDOWN });
        } else {
          setRetryState({ ...retryState, status: RetryStatus.FAILED });
        }
        break;
      case RetryStatus.COUNTDOWN:
        break;
      case RetryStatus.ACTIVE:
        void callCheckUserPermissions();
        break;
      case RetryStatus.FAILED:
        // Tell user we couldn't get it done
        break;
    }
  }, [retryState]);

  return (
    <LoginContainer style={{ flexDirection: "column", textAlign: "center" }}>
      <>
        <Heading as="h1">{t("Får ikke kontakt med tjener")}</Heading>
        <br />
        <Heading as="h2" variant="md">
          {t(
            "Drops Dashbord får ikke kontakt med tjener. Dette kan skyldes nettverksproblemer.",
          )}
        </Heading>
        <Heading as="h2" variant="md">
          {t("Vennligst ta kontakt med IT")}
          <TextLink
            href={mailLinkWithError(t, error)}
            target="_blank"
            rel="noopener noreferer"
            style={{ fontSize: "24px", margin: "0 0.5ch", display: "inline" }}
          >
            {`${t("her")}.`}
          </TextLink>
        </Heading>
        <br />
        <br />
      </>
      {retryState.status !== RetryStatus.FAILED ? (
        <LightSpinnerStyled width="58px">
          {retryText(retryState, t)}
        </LightSpinnerStyled>
      ) : (
        <>
          <Heading as="h2" variant="md">
            {t(
              "Beklager, men vi lyktes ikke med å koble til. Vennligst logg ut og prøv igjen.",
            )}
          </Heading>
          <Button
            onClick={() => window.location.reload()}
            mt="30px"
            size="lg"
            width="180px"
          >
            {t("Prøv på nytt")}
          </Button>
        </>
      )}
    </LoginContainer>
  );
};

export default NetworkError;
